import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import { color } from 'styles/utils';
import MuiButton from 'components/common/button/MuiButton';
import CustomSpan from 'components/common/text/CustomSpan';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';

import { useShallowAppSelector } from 'store/hooks';
import { UserActions, UtilActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import Color from 'color';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { TextField } from '@material-ui/core';
import { getStringByte, validInputString } from 'lib/library';
import { useTranslation } from 'react-i18next';

interface RemakeTypeEditModalProps {
  onCloseModal: () => void;
}

interface RemakeTypeListItemProps {
  remakeType: any;
  onChangeRemakeContents: (value: string, index: number) => void;
}

function RemakeTypeEditModalContainer({ onCloseModal }: RemakeTypeEditModalProps) {
  const {
    fetchMyRemakeList: { list: remakeList },
    fetchMyRemakeListSuccess,
    editMyRemakeListSuccess,
  }: {
    fetchMyRemakeList: { list: any[] };
    fetchMyRemakeListSuccess: boolean;
    editMyRemakeListSuccess: boolean;
  } = useShallowAppSelector(state => ({
    fetchMyRemakeList: state.user.fetchMyRemakeList.data || {},
    fetchMyRemakeListSuccess: state.user.fetchMyRemakeList.success,
    editMyRemakeListSuccess: state.user.editMyRemakeList.success,
  }));

  const [remakeListCt, setRemakeListCt] = useState<Object[]>([]);
  const [remakeListChangeCt, setRemakeListChangeCt] = useState<Object[]>([]);
  const [isModify, setIsModify] = useState<boolean>(false);

  useEffect(() => {
    UserActions.fetch_my_remake_list_request();
  }, []);

  useDidUpdateEffect(() => {
    if (fetchMyRemakeListSuccess) {
      setRemakeListCt([...remakeList]);
      setRemakeListChangeCt([...remakeList]);
    }
  }, [fetchMyRemakeListSuccess]);

  useDidUpdateEffect(() => {
    if (editMyRemakeListSuccess) {
      UserActions.fetch_my_remake_list_request();
      setIsModify(false);
    }
  }, [editMyRemakeListSuccess]);

  const handleCancelRemakeEdit = () => {
    setIsModify(false);
    setRemakeListCt([...remakeList]);
  };
  const handleChangeRemakeContents = (value: string, index: number) => {
    const changeValue: any[] = remakeListCt.map((item: any) => {
      let returnvalue = {};
      if (item.remakeTypeIdx === index) {
        returnvalue = {
          remakeTypeIdx: index,
          remakeTypeName: value,
          enrollDate: null,
          modifyDate: null,
        };
      } else {
        returnvalue = item;
      }
      return returnvalue;
    });
    setRemakeListChangeCt([...changeValue]);
  };

  const handleSaveRemakeType = () => {
    const submitData = {
      remakeTypeList: [...remakeListChangeCt],
    };
    UserActions.edit_my_remake_list_request(submitData);
  };
  return (
    <RemakeTypeEditModal data-component-name="RemakeTypeEditModal">
      <div className="RemakeTypeEditModal__container">
        <div className="RemakeTypeEditModal__comment">
          <p>
            <T>REMAKE_TYPE_DETAIL</T>
          </p>
        </div>
        <div className="RemakeTypeEditModal__table">
          {remakeListCt.map((item: any) => {
            return (
              <div className="RemakeTypeEditModal__table_item remakeType" key={item.remakeTypeIdx}>
                <div className="remakeType_index">
                  <CustomSpan fontSize={14} fontColor="#fff">
                    {item.remakeTypeIdx}
                  </CustomSpan>
                </div>
                <div className="remakeType_contents">
                  {!isModify ? (
                    <CustomSpan fontSize={16}>
                      {!!item.remakeTypeName ? item.remakeTypeName : '-'}
                    </CustomSpan>
                  ) : (
                    <RemakeTypeListItemComponent
                      remakeType={item}
                      onChangeRemakeContents={handleChangeRemakeContents}
                    />
                  )}
                </div>
              </div>
            );
          }, [])}
        </div>
        {!isModify ? (
          <StyledPlainButtonOuter
            backgroundColor={color.navy_blue}
            left="50%"
            width={370}
            height={76}
          >
            <MuiButton
              type="submit"
              config={{
                width: '320px',
              }}
              disableElevation
              color="primary"
              variant="contained"
              disablebackground="#17288a"
              disablefontcolor="#ffffff"
              className="xl border-radius-round RemakeTypeEditModal__modify_btn"
              onClick={() => {
                setIsModify(true);
              }}
            >
              <CustomSpan fontSize={16}>
                <T>GLOBAL_MODIFY</T>
              </CustomSpan>
            </MuiButton>
          </StyledPlainButtonOuter>
        ) : (
          <StyledPlainButtonOuter
            backgroundColor={color.navy_blue}
            left="50%"
            width={370}
            height={76}
          >
            <div className="RemakeTypeEditModal__button_box">
              <MuiButton
                config={{
                  width: '95px',
                  color: color.gray_week,
                }}
                disableElevation
                variant="contained"
                className="xl border-radius-round inset-shadow-default RemakeTypeEditModal__cancel_btn"
                onClick={() => {
                  handleCancelRemakeEdit();
                }}
              >
                <CustomSpan fontSize={16}>
                  <T>GLOBAL_CANCEL</T>
                </CustomSpan>
              </MuiButton>
              <MuiButton
                config={{
                  width: '235px',
                }}
                disableElevation
                variant="contained"
                className="xl border-radius-round inset-shadow-default RemakeTypeEditModal__save_btn"
                onClick={() => {
                  handleSaveRemakeType();
                }}
              >
                <span className="btn-shadow-inset"></span>
                <CustomSpan fontSize={16}>
                  <T>GLOBAL_SAVE</T>
                </CustomSpan>
              </MuiButton>
            </div>
          </StyledPlainButtonOuter>
        )}
      </div>
    </RemakeTypeEditModal>
  );
}

function RemakeTypeListItemComponent({
  remakeType,
  onChangeRemakeContents,
}: RemakeTypeListItemProps) {
  const { t } = useTranslation();
  const [name, setName] = useState<string>(remakeType.remakeTypeName);
  const [index, setIndex] = useState<number>(remakeType.remakeTypeIdx);

  const handleChangeRemakeContentsValue = (value: string, index: number) => {
    const trimValue = value.trim();
    if (getStringByte(trimValue) > 30) return;
    setName(trimValue);
    onChangeRemakeContents(trimValue, index);
  };

  return (
    <RemakeTypeListItem data-component-name="RemakeTypeListItem">
      {useMemo(
        () => (
          <MuiWrapper>
            <TextField
              className="radius-md RemakeTypeListItem__input"
              variant="outlined"
              fullWidth
              type="email"
              placeholder={t('GLOBAL_REMAKE_TYPE_PLACEHOLDER')}
              value={name || ''}
              onChange={e => {
                const checkText = validInputString(e.target.value);
                handleChangeRemakeContentsValue(checkText, index);
              }}
            />
          </MuiWrapper>
        ),
        [name],
      )}
    </RemakeTypeListItem>
  );
}

const RemakeTypeEditModal = styled.section<{}>`
  .RemakeTypeEditModal__container {
    width: 100%;
    border-radius: 15px;
    padding: 50px 30px 38px;
    background-color: #ffffff;
    position: relative;
    .RemakeTypeEditModal__comment {
      margin-bottom: 50px;
      p {
        font-size: 19px;
        font-weight: 700;
        color: #303030;
      }
    }
    .RemakeTypeEditModal__table {
      /* padding: 0 30px; */
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      row-gap: 20px;
      margin-bottom: 140px;
      .RemakeTypeEditModal__table_item {
        width: 590px;
        /* width: 500px; */
        height: 90px;
        border: 1px solid #dddee4;
        border-radius: 5px;
        display: flex;
        align-items: center;
        .remakeType_index {
          margin: 0 40px 0 30px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background-color: #00a6e2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }

    /* .partnerModalRequest__button_container { */
    .RemakeTypeEditModal__button_box {
      position: relative;
      display: flex;
      .RemakeTypeEditModal__cancel_btn {
        position: relative;
        z-index: 2;
      }
      .RemakeTypeEditModal__save_btn {
        margin-left: -10px;
        background: rgb(0, 166, 226);
        background: linear-gradient(90deg, rgba(0, 166, 226, 1) 0%, rgba(8, 123, 238, 1) 100%);
        border: none;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        overflow: hidden;
        &:hover {
          background: ${`linear-gradient(
            90deg,
            ${Color('rgba(0, 166, 226, 1)').darken(0.12)} 0%,
            ${Color('rgba(8, 123, 238, 1)').darken(0.12)} 100%
          )`};
        }
        .btn-shadow-inset {
          position: absolute;
          top: -5px;
          left: -46px;
          width: 66px;
          height: 66px;
          border-radius: 33px;
          background-color: ${color.navy_blue};
          box-shadow: 3px 3px 6px rgba(0, 0, 0, 0.16);
        }
      }
    }
    /* } */
  }
`;

const RemakeTypeListItem = styled.section<{}>`
  .RemakeTypeListItem__input {
    width: 460px;
    font-size: 16px;
  }
`;
export default React.memo(RemakeTypeEditModalContainer);
